import React, { useCallback, useEffect, useRef, useState } from "react"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../../DashboardComponents/DashboardCard"
import CustomButton from "../../OnboardingComponents/CustomButton"
import DeleteConfirmation from "../../Common/DeleteConfirmation"
import { ICONS } from "../../../constants/icons"
import { del, get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { resolveMediaUrl } from "../../../helpers/userAvatar"

const MAX_MEDIA = 9
const MAX_BYTES = 5 * 1024 * 1024
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "image/avif",
  "image/heic",
]
const ACCEPT_ATTR = ACCEPTED_TYPES.join(",")

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const isVideoMime = (mime = "") =>
  String(mime).toLowerCase().startsWith("video/")

const mapProjectMedia = item => {
  if (!item) return null

  const nested = item.media && typeof item.media === "object" ? item.media : null
  const src =
    resolveMediaUrl(nested?.image) ||
    resolveMediaUrl(nested?.thumbnail) ||
    resolveMediaUrl(item.path_url) ||
    resolveMediaUrl(item.media_url) ||
    resolveMediaUrl(item.url) ||
    resolveMediaUrl(item.file_url) ||
    resolveMediaUrl(item.thumbnail_path_url) ||
    resolveMediaUrl(item.image) ||
    resolveMediaUrl(item.thumbnail) ||
    resolveMediaUrl(item.path) ||
    resolveMediaUrl(item.file) ||
    (typeof item.file === "object"
      ? resolveMediaUrl(item.file?.path_url) ||
        resolveMediaUrl(item.file?.url) ||
        resolveMediaUrl(item.file?.path)
      : "") ||
    ""

  const mimeType =
    item.mime_type ||
    item.mimeType ||
    item.file?.mime_type ||
    item.file?.mimeType ||
    ""

  const type = String(item.type || "").toLowerCase()
  const isVideo = type === "video" || isVideoMime(mimeType)

  if (!item.id && !src) return null

  return {
    id: item.id || src,
    src,
    mimeType,
    isVideo,
  }
}

const normalizeMediaList = payload => {
  const result = payload?.data?.result ?? payload?.result ?? payload
  const list = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : []
  return list
    .map(mapProjectMedia)
    .filter(item => item?.id && item?.src)
    .slice(0, MAX_MEDIA)
}

/**
 * MediaCard — Past project media.
 * Empty: Figma upload dropzone. Filled: 3×3 grid + header Upload.
 */
const MediaCard = ({ projectId, fullHeight = true }) => {
  const inputRef = useRef(null)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(Boolean(projectId))
  const [isUploading, setIsUploading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const canUpload =
    Boolean(projectId) && items.length < MAX_MEDIA && !isUploading

  const fetchMedia = useCallback(async () => {
    if (!projectId) {
      setItems([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const response = await get(endpoints.getProjectMedia, {
        params: {
          project_id: projectId,
          page: 1,
          page_size: MAX_MEDIA,
          "with[]": ["uploader"],
        },
        skipErrorToast: true,
      })

      if (response?.data?.success) {
        setItems(normalizeMediaList(response))
      } else {
        setItems([])
      }
    } catch {
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchMedia()
  }, [fetchMedia])

  const openFilePicker = () => {
    if (!canUpload) return
    inputRef.current?.click()
  }

  const handleFileChange = async event => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || !projectId) return

    if (items.length >= MAX_MEDIA) {
      toast.error(`You can upload up to ${MAX_MEDIA} photos.`, TOAST_OPTIONS)
      return
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error(
        "Use PNG, JPG, WEBP, GIF, SVG, AVIF, or HEIC images only.",
        TOAST_OPTIONS,
      )
      return
    }

    if (file.size > MAX_BYTES) {
      toast.error("Each photo must be 5MB or less.", TOAST_OPTIONS)
      return
    }

    const previewUrl = URL.createObjectURL(file)
    const tempId = `temp-${Date.now()}`
    const optimisticItem = {
      id: tempId,
      src: previewUrl,
      mimeType: file.type,
      isVideo: false,
      isOptimistic: true,
    }

    setItems(prev => [optimisticItem, ...prev].slice(0, MAX_MEDIA))
    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("project_id", projectId)

      const response = await post(endpoints.createProjectMedia, formData)
      if (!response?.data?.success) {
        setItems(prev => prev.filter(item => item.id !== tempId))
        URL.revokeObjectURL(previewUrl)
        toastApiError(response, "Failed to upload photo")
        return
      }

      const created = mapProjectMedia(response.data.result)
      if (created?.src) {
        setItems(prev =>
          [created, ...prev.filter(item => item.id !== tempId)].slice(
            0,
            MAX_MEDIA,
          ),
        )
      }

      toast.success(
        response.data.message || "Photo uploaded successfully.",
        TOAST_OPTIONS,
      )
      await fetchMedia()
      URL.revokeObjectURL(previewUrl)
    } catch (err) {
      setItems(prev => prev.filter(item => item.id !== tempId))
      URL.revokeObjectURL(previewUrl)
      toastApiError(err, "Failed to upload photo")
    } finally {
      setIsUploading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deletingId || isDeleting) return
    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteProjectMedia(deletingId))
      if (!response?.data?.success) {
        toastApiError(response, "Failed to delete photo")
        return
      }
      setItems(prev => prev.filter(item => item.id !== deletingId))
      toast.success(
        response.data.message || "Photo deleted successfully.",
        TOAST_OPTIONS,
      )
      setDeletingId(null)
    } catch (err) {
      toastApiError(err, "Failed to delete photo")
    } finally {
      setIsDeleting(false)
    }
  }

  const hasItems = items.length > 0
  const showUploadZone = !isLoading && !hasItems
  const showGrid = !isLoading && hasItems

  const uploadActions = showGrid ? (
    <div className="project-overview__media-actions">
      <span className="project-overview__media-limit">Upload upto 9 files</span>
      <CustomButton
        variant="secondary"
        size="sm"
        type="button"
        trailingIconSrc={ICONS.callUploadExport}
        trailingIconAlt=""
        className="project-overview__media-upload-btn"
        onClick={openFilePicker}
        disabled={!canUpload}
        loading={isUploading}
      >
        Upload
      </CustomButton>
    </div>
  ) : null

  return (
    <div className="project-overview__media-root">
      <DashboardCard
        title="Media"
        tone="lightteal"
        fullHeight={fullHeight}
        className={`dcard--no-spacing dcard--no-justify project-overview__media-card${
          showGrid ? " project-overview__media-card--filled" : ""
        }`}
        topRight={uploadActions}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="project-overview__media-input"
          onChange={handleFileChange}
          disabled={!canUpload}
        />

        {isLoading ? (
          <div className="project-overview__media-skeleton" aria-hidden="true" />
        ) : null}

        {showUploadZone ? (
          <button
            type="button"
            className="project-overview__media-upload"
            onClick={openFilePicker}
            disabled={!canUpload}
            aria-label="Upload project photos"
          >
            <span
              className="project-overview__media-upload-icon"
              aria-hidden="true"
            >
              <img src={ICONS.callUploadExport} alt="" width={20} height={20} />
            </span>
            <span className="project-overview__media-upload-title">
              {isUploading ? "Uploading…" : "Upload project photos"}
            </span>
            <span className="project-overview__media-upload-hint">
              PNG, JPG (up to 9 photos, max 5MB each)
            </span>
          </button>
        ) : null}

        {showGrid ? (
          <div className="project-overview__media-grid">
            {items.map(item => (
              <div
                key={item.id}
                className={`project-overview__media-item${
                  item.isOptimistic
                    ? " project-overview__media-item--pending"
                    : ""
                }`}
              >
                <img src={item.src} alt="" />
                {item.isVideo ? (
                  <span
                    className="project-overview__media-play"
                    aria-hidden="true"
                  >
                    <img src={ICONS.play} alt="" width={14} height={14} />
                  </span>
                ) : null}
                {!item.isOptimistic ? (
                  <button
                    type="button"
                    className="project-overview__media-remove"
                    aria-label="Delete photo"
                    onClick={() => setDeletingId(item.id)}
                    disabled={isUploading || isDeleting}
                  >
                    <img src={ICONS.trash} alt="" width={14} height={14} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </DashboardCard>

      <DeleteConfirmation
        isOpen={Boolean(deletingId)}
        onCancel={() => {
          if (isDeleting) return
          setDeletingId(null)
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Photo"
        message="Are you sure you want to delete this project photo? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete Photo"
        confirmLoadingText="Deleting..."
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default MediaCard
